import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  InverterType,
  NewExistingType,
  DomesticStatus,
  PanelOrientation,
  ElectricConfig,
} from '../../types';
import { PackageItemState } from '../../types/lender-packages.types';
import { NetsuiteItemState } from '../../types/netsuite-items.types';
import { EssConfigValue, InstallationMethod, DEFAULT_INSTALLATION_METHOD, BomType, DEFAULT_BOM_TYPE } from '../../constants/lender-packages.constants';
import { StructureRoofDetail, AuroraPanelPoolRow, stripDesignSummary, stripRoofDesignSummary } from '../../utils/aurora-mapper.utils';
import { SystemKey, SYSTEM_KEYS } from '../../constants/aurora.constants';

export enum WorkflowStep {
  TASK = 'task',
  EQUIPMENT = 'equipment',
  ELECTRICAL = 'electrical',
  MOUNTING = 'mounting',
  BOM = 'bom',
  DOCUMENTATION = 'documentation',
  OUTPUT = 'output'
}

export enum CROutputTab {
  LIVE = 'live',
  EDITABLE = 'editable',
  DIFF = 'diff'
}

/** Lightweight task reference stored in Redux after task selection */
export interface SelectedTaskStub {
  gid: string;
  name: string;
  /** Client raw string extracted from task custom fields (Asana: "Client"/"Client:", ClickUp: "[GEN] Client") */
  client?: string;
  /** Resolved DB client UUID (matched by name substring against getClients() result) */
  client_id?: string;
  /** Resolved DB client display name */
  client_name?: string;
}

export type { SystemKey } from '../../constants/aurora.constants';
export { SYSTEM_KEYS, SYSTEM_LABELS } from '../../constants/aurora.constants';

interface SelectedInverter {
  model: string;
  wattage?: number;
  current?: number;
  quantity: number;
}

export interface SystemEquipment {
  module: {
    selected_brand?: string;
    selected_module?: {
      fullName?: string;
      wattage?: number;
      modelNumber?: string;
      width?: number;
      height?: number;
    };
    quantity: number;
  };
  inverters: {
    selected_type?: InverterType;
    selected_brand?: {
      name: string;
      inverter_type: InverterType;
    };
    selected_inverters: SelectedInverter[];
    selected_optimizer?: {
      type: string;
      current?: number;
    };
    optimizer_quantity: number;
    rsd?: {
      type: string;
      quantity: number;
      current: string;
    };
    combiner: string;
  };
  batteries: {
    selected_brand?: string;
    selected_battery?: {
      battery_name: string;
      battery_model: string;
      kwh?: number;
      current?: number;
      wattage?: number;
    };
    quantity: number;
    is_domestic: boolean;
  };
  strings: Record<string, string>;
}

interface CustomerInfo {
  design_date: string;
  customer_name: string;
  street_number: string;
  street_name: string;
  city: string;
  state_short_name: string;
  zip_code: string;
  utility_name: string;
  utility_meter_number: string;
  ahj_name: string;
  texas_esi_id: string;
  parcel_number_apn: string;
}

const INITIAL_SYSTEM_EQUIPMENT: SystemEquipment = {
  module: {
    selected_brand: '',
    selected_module: {},
    quantity: 0
  },
  inverters: {
    selected_type: InverterType.INVERTER,
    selected_inverters: [],
    optimizer_quantity: 0,
    rsd: { type: '', quantity: 0, current: '' },
    combiner: ''
  },
  batteries: {
    quantity: 0,
    is_domestic: false
  },
  strings: { string_1: '' }
};

interface CreateCRState {
  // Workflow navigation
  workflow: {
    current_step: WorkflowStep;
    completed_steps: WorkflowStep[];
    validation_attempted: { [key: string]: boolean };
  };

  // Aurora design data
  aurora_data: {
    design_summary: Record<string, any> | null;
    roof_design_summary: Record<string, any> | null;
    structures_roofs_details: StructureRoofDetail[] | null;
    aurora_panel_rows_pool: AuroraPanelPoolRow[] | null;
  };

  // Organized form data with clear sections
  form_data: {
    task: {
      selected_task?: SelectedTaskStub;
      aurora_design_id: string;
      customer_info: CustomerInfo;
    };

    equipment: {
      active_system: SystemKey;
      system_one: SystemEquipment;
      system_two: SystemEquipment;
      system_three: SystemEquipment;
      production: {
        production_value: string;
      };
    };

    electrical: {
      // MSP
      msp: {
        buss_rating?: number;
        main_breaker_rating?: number;
        manufacturer?: string;
        location?: string;
        style?: string;
        type?: string;
        cad_location?: string;
        msp_new_existing?: NewExistingType | null;
        main_breaker_new_existing?: NewExistingType | null;
      };

      // MBE
      mbe: {
        buss_rating?: number;
        main_breaker_rating?: number;
        mbe_new_existing?: NewExistingType | null;
        main_breaker_new_existing?: NewExistingType | null;
      };

      // BLP
      blp: {
        buss_rating?: number;
        main_breaker_rating?: number;
        blp_new_existing?: NewExistingType | null;
        main_breaker_new_existing?: NewExistingType | null;
      };

      // ATS
      ats: {
        buss_rating?: number;
        main_breaker_rating?: number;
        ats_new_existing?: NewExistingType | null;
        main_breaker_new_existing?: NewExistingType | null;
        panel_name?: string;
        generator_name?: string;
      };

      // Production Meter
      production_meter: {
        new_existing?: NewExistingType | null;
        meter_name_model?: string;
      };

      // Utility
      utility: {
        service_feed_source?: string;
        distance_from_um_callout?: string;
        utility_meter_base_new_existing?: NewExistingType | null;
        utility_meter_base_name_model?: string;
      };

      // Subpanel
      subpanel: {
        subpanel_new_existing?: NewExistingType | null;
        main_breaker_new_existing?: NewExistingType | null;
        subpanel_size?: string;
        main_breaker_rating?: number;
        feeding_method?: string;
        ocpd_size?: string;
        pv_breaker_size?: string;
        pv_breaker_location?: string;
      };

      // POI
      poi: {
        selected_poi?: string;
        location?: string;
        breaker_size?: number;
      };

      // Multiple DISCO configurations (up to 5)
      disco_1?: {
        rating?: number;
        type?: string;
        model?: string;
        fuses?: number;
        location?: string;
        text?: string;
      };
      disco_2?: {
        rating?: number;
        type?: string;
        model?: string;
        fuses?: number;
        location?: string;
        text?: string;
      };
      disco_3?: {
        rating?: number;
        type?: string;
        model?: string;
        fuses?: number;
        location?: string;
        text?: string;
      };
      disco_4?: {
        rating?: number;
        type?: string;
        model?: string;
        fuses?: number;
        location?: string;
        text?: string;
      };
      disco_5?: {
        rating?: number;
        type?: string;
        model?: string;
        fuses?: number;
        location?: string;
        text?: string;
      };

      // Additional Electric Configuration
      aec_type?: ElectricConfig;
      new_main_breaker_size?: ElectricConfig;
      subpanel_size?: ElectricConfig;
      feeding_method?: ElectricConfig;
      ocpd_size?: ElectricConfig;
      pv_breaker_size?: ElectricConfig;
      pv_breaker_location?: ElectricConfig;
    };

    structural: {
      // General Configuration
      general_configuration: {
        mount_system?: string;
        roof_mount_conduit_size?: string;
        roof_mount_conduit_type?: string;
        bos_conduit?: string;
        trench?: {
          conduit_size?: string;
          conduit_type?: string;
          length?: string;
          depth?: string;
        };
      };

      // Bill of Material
      bill_of_material: {
        total_rails: number;
        total_splice_kits: number;
        total_mid_clamps: number;
        total_end_clamps: number;
        total_grounding_lugs: number;
        is_manual_override: boolean;
      };

      // Multiple Structures (up to 3)
      structures: {
        structure_1?: {
          name?: string;
          framing?: {
            size?: string;
            type?: string;
            rafter_spacing?: string;
            is_assumed?: boolean;
          };
          attachments_rails?: {
            attachment_1?: string;
            rail_1?: {
              name?: string;
              domestic_status?: DomesticStatus;
              is_domestic?: boolean;
              length?: number;
            };
            attachment_2?: string;
            rail_2?: {
              name?: string;
              domestic_status?: DomesticStatus;
              is_domestic?: boolean;
              length?: number;
            };
            panel_rows_1?: {
              rows: Array<{
                id: string;
                orientation: PanelOrientation;
                panel_count: number;
                system_key?: SystemKey;
              }>;
            };
            panel_rows_2?: {
              rows: Array<{
                id: string;
                orientation: PanelOrientation;
                panel_count: number;
                system_key?: SystemKey;
              }>;
            };
          };
          roof_configuration?: {
            roof_type?: string;
            roof_condition?: string;
            reroof_status?: string;
            building_height?: string;
          };
        };
        structure_2?: {
          name?: string;
          framing?: {
            size?: string;
            type?: string;
            rafter_spacing?: string;
            is_assumed?: boolean;
          };
          attachments_rails?: {
            attachment_1?: string;
            rail_1?: {
              name?: string;
              domestic_status?: DomesticStatus;
              is_domestic?: boolean;
              length?: number;
            };
            attachment_2?: string;
            rail_2?: {
              name?: string;
              domestic_status?: DomesticStatus;
              is_domestic?: boolean;
              length?: number;
            };
            panel_rows_1?: {
              rows: Array<{
                id: string;
                orientation: PanelOrientation;
                panel_count: number;
                system_key?: SystemKey;
              }>;
            };
            panel_rows_2?: {
              rows: Array<{
                id: string;
                orientation: PanelOrientation;
                panel_count: number;
                system_key?: SystemKey;
              }>;
            };
          };
          roof_configuration?: {
            roof_type?: string;
            roof_condition?: string;
            reroof_status?: string;
            building_height?: string;
          };
        };
        structure_3?: {
          name?: string;
          framing?: {
            size?: string;
            type?: string;
            rafter_spacing?: string;
            is_assumed?: boolean;
          };
          attachments_rails?: {
            attachment_1?: string;
            rail_1?: {
              name?: string;
              domestic_status?: DomesticStatus;
              is_domestic?: boolean;
              length?: number;
            };
            attachment_2?: string;
            rail_2?: {
              name?: string;
              domestic_status?: DomesticStatus;
              is_domestic?: boolean;
              length?: number;
            };
            panel_rows_1?: {
              rows: Array<{
                id: string;
                orientation: PanelOrientation;
                panel_count: number;
                system_key?: SystemKey;
              }>;
            };
            panel_rows_2?: {
              rows: Array<{
                id: string;
                orientation: PanelOrientation;
                panel_count: number;
                system_key?: SystemKey;
              }>;
            };
          };
          roof_configuration?: {
            roof_type?: string;
            roof_condition?: string;
            reroof_status?: string;
            building_height?: string;
          };
        };
      };
    };

    bom: {
      /** BOM workflow type — set on task selection (auto-detected from client) or manually switched by user */
      bom_type: BomType;

      /** Standard mode BOM values (calculated or manual override) */
      standard: {
        is_manual_override: boolean;
        total_rails: number;
        total_splice_kits: number;
        total_mid_clamps: number;
        total_end_clamps: number;
        total_grounding_lugs: number;
      };

      /** LGCY client BOM — fully independent state */
      lgcy: {
        installation_method: InstallationMethod;
        lis_number: string;
        ess_config: EssConfigValue | '';
        lender: { id: string; name: string };
        package: { id: string; name: string; key: string; auto_enphase_accessories?: boolean };
        lender_package_items: PackageItemState[];
        client_netsuite_items: NetsuiteItemState[];
        input_fields: {
          is_manual_override: boolean;
          total_rows: number;
          arrays: number;
          strings_circuits: number;
          mci_s: number;
        };
        bom_results: {
          is_manual_override: boolean;
          sum_of_panels: number;
          rails_in_total: number;
          end_cap: number;
          splice_kit: number;
          mid_clamps_total: number;
          end_clamps_total: number;
          washer_lag: number;
          estimated_attachments: number;
          universal_wire_clamp: number;
          wire_retention_clip: number;
          junction_box: number;
          grounding_lugs: number;
          enphase: {
            cable_landscape: number;
            cable_female_connector: number;
            cable_male_connector: number;
            cable_sealing_cap: number;
            cable_terminator_cap: number;
          } | null;
        };
      };
    };

    documentation: {
      bos_location_value: string;
      ahj_value: string;
      um_number_value: string;
      install_notes_value: string;
      miscellaneous_notes_value: string;
      aurora_value: string;
      gdrive_link_value: string;
      notion_ahj_value: string;
      notion_ux_value: string;
      notion_cx_value: string;
      nec_value: string;
      ibc_value: string;
      irc_value: string;
      ifc_value: string;
      poi_notes_value: string;
      design_notes_value: string;
      lgcy_lis_number_value: string;
    };
  };

  // CR Output state
  cr_output: {
    generated_cr: Record<string, any>;
    updated_cr: Record<string, any>;
    changes: Record<string, any>;
    active_tab: CROutputTab;
  };
}

const initialState: CreateCRState = {
  workflow: {
    current_step: WorkflowStep.TASK,
    completed_steps: [],
    validation_attempted: {}
  },
  aurora_data: {
    design_summary: null,
    roof_design_summary: null,
    structures_roofs_details: null,
    aurora_panel_rows_pool: null,
  },
  form_data: {
    task: {
      aurora_design_id: '',
      customer_info: {
        design_date: '',
        customer_name: '',
        street_number: '',
        street_name: '',
        city: '',
        state_short_name: '',
        zip_code: '',
        utility_name: '',
        utility_meter_number: '',
        ahj_name: '',
        texas_esi_id: '',
        parcel_number_apn: ''
      }
    },
    equipment: {
      active_system: 'system_one',
      system_one: { ...INITIAL_SYSTEM_EQUIPMENT, strings: { string_1: '' } },
      system_two: { ...INITIAL_SYSTEM_EQUIPMENT, strings: { string_1: '' } },
      system_three: { ...INITIAL_SYSTEM_EQUIPMENT, strings: { string_1: '' } },
      production: {
        production_value: ''
      }
    },
    electrical: {
      msp: {},
      mbe: {},
      blp: {},
      ats: {},
      production_meter: {},
      utility: {},
      subpanel: {},
      poi: {}
    },
    structural: {
      general_configuration: {},
      bill_of_material: {
        total_rails: 0,
        total_splice_kits: 0,
        total_mid_clamps: 0,
        total_end_clamps: 0,
        total_grounding_lugs: 0,
        is_manual_override: false
      },
      structures: {}
    },
    bom: {
      bom_type: DEFAULT_BOM_TYPE,
      standard: {
        is_manual_override: false,
        total_rails: 0,
        total_splice_kits: 0,
        total_mid_clamps: 0,
        total_end_clamps: 0,
        total_grounding_lugs: 0,
      },
      lgcy: {
        installation_method: DEFAULT_INSTALLATION_METHOD,
        lis_number: '',
        ess_config: '',
        lender: { id: '', name: '' },
        package: { id: '', name: '', key: '', auto_enphase_accessories: false },
        lender_package_items: [],
        client_netsuite_items: [],
        input_fields: {
          is_manual_override: false,
          total_rows: 0,
          arrays: 0,
          strings_circuits: 0,
          mci_s: 0,
        },
        bom_results: {
          is_manual_override: false,
          sum_of_panels: 0,
          rails_in_total: 0,
          end_cap: 0,
          splice_kit: 0,
          mid_clamps_total: 0,
          end_clamps_total: 0,
          washer_lag: 0,
          estimated_attachments: 0,
          universal_wire_clamp: 0,
          wire_retention_clip: 0,
          junction_box: 0,
          grounding_lugs: 0,
          enphase: null as { cable_landscape: number; cable_female_connector: number; cable_male_connector: number; cable_sealing_cap: number; cable_terminator_cap: number } | null,
        },
      },
    },
    documentation: {
      bos_location_value: '',
      ahj_value: '',
      um_number_value: '',
      install_notes_value: '',
      miscellaneous_notes_value: '',
      aurora_value: '',
      gdrive_link_value: '',
      notion_ahj_value: '',
      notion_ux_value: '',
      notion_cx_value: '',
      nec_value: '',
      ibc_value: '',
      irc_value: '',
      ifc_value: '',
      poi_notes_value: '',
      design_notes_value: '',
      lgcy_lis_number_value: ''
    }
  },
  cr_output: {
    generated_cr: {},
    updated_cr: {},
    changes: {},
    active_tab: CROutputTab.LIVE
  },
};

const createCRSlice = createSlice({
  name: 'createCR',
  initialState,
  reducers: {
    // Workflow navigation
    setCurrentStep: (state, action: PayloadAction<WorkflowStep>) => {
      state.workflow.current_step = action.payload;
      // Switch to Live Preview when entering output step
      if (action.payload === WorkflowStep.OUTPUT) {
        state.cr_output.active_tab = CROutputTab.LIVE;
      }
    },

    setCompletedSteps: (state, action: PayloadAction<WorkflowStep[]>) => {
      state.workflow.completed_steps = action.payload;
    },

    addCompletedStep: (state, action: PayloadAction<WorkflowStep>) => {
      if (!state.workflow.completed_steps.includes(action.payload)) {
        state.workflow.completed_steps.push(action.payload);
      }
    },

    setValidationAttempted: (state, action: PayloadAction<{ step: string; attempted: boolean }>) => {
      state.workflow.validation_attempted[action.payload.step] = action.payload.attempted;
    },

    // Task selection
    setSelectedTask: (state, action: PayloadAction<SelectedTaskStub | undefined>) => {
      state.form_data.task.selected_task = action.payload;
    },
    clearSelectedTask: (state) => {
      state.form_data.task.selected_task = undefined;
      state.form_data.task.aurora_design_id = '';
      state.form_data.task.customer_info = {
        ...initialState.form_data.task.customer_info
      };
      state.aurora_data = { design_summary: null, roof_design_summary: null, structures_roofs_details: null, aurora_panel_rows_pool: null };
    },

    // Aurora design data
    setAuroraDesignId: (state, action: PayloadAction<string>) => {
      state.form_data.task.aurora_design_id = action.payload;
    },
    setAuroraData: (state, action: PayloadAction<{
      design_summary: Record<string, any>;
      roof_design_summary: Record<string, any>;
      structures_roofs_details?: StructureRoofDetail[] | null;
    }>) => {
      const { design_summary, roof_design_summary } = action.payload;

      state.aurora_data = {
        design_summary: design_summary ? stripDesignSummary(design_summary) : null,
        roof_design_summary: roof_design_summary ? stripRoofDesignSummary(roof_design_summary) : null,
        structures_roofs_details: action.payload.structures_roofs_details ?? null,
        aurora_panel_rows_pool: null,
      };
      // Reset equipment and structures — new Aurora design replaces previous data
      state.form_data.equipment = { ...initialState.form_data.equipment };
      state.form_data.structural.structures = {};
    },
    clearAuroraData: (state) => {
      state.form_data.task.aurora_design_id = '';
      state.aurora_data = { design_summary: null, roof_design_summary: null, structures_roofs_details: null, aurora_panel_rows_pool: null };
      // Reset equipment populated from Aurora BOM
      state.form_data.equipment = { ...initialState.form_data.equipment };
      // Reset structural structures (names + assigned panel rows from Aurora)
      state.form_data.structural.structures = {};
    },
    setStructuresRoofsDetails: (state, action: PayloadAction<StructureRoofDetail[]>) => {
      state.aurora_data.structures_roofs_details = action.payload;
    },
    setAuroraPanelRowsPool: (state, action: PayloadAction<AuroraPanelPoolRow[] | null>) => {
      state.aurora_data.aurora_panel_rows_pool = action.payload;
    },

    // Customer info
    updateCustomerInfo: (state, action: PayloadAction<Partial<CustomerInfo>>) => {
      Object.assign(state.form_data.task.customer_info, action.payload);
    },

    // Active system selection
    setActiveSystem: (state, action: PayloadAction<SystemKey>) => {
      state.form_data.equipment.active_system = action.payload;
    },

    // Module selection - targets active system
    updateModuleData: (state, action: PayloadAction<{
      selected_brand?: string;
      selected_module?: {
        fullName?: string;
        wattage?: number;
        modelNumber?: string;
        width?: number;
        height?: number;
      };
      quantity?: number;
    }>) => {
      const system = state.form_data.equipment.active_system;
      if ('selected_brand' in action.payload) {
        state.form_data.equipment[system].module.selected_brand = action.payload.selected_brand;
      }
      if ('selected_module' in action.payload) {
        state.form_data.equipment[system].module.selected_module = action.payload.selected_module;
      }
      if (action.payload.quantity !== undefined) {
        state.form_data.equipment[system].module.quantity = action.payload.quantity;
      }
    },

    // Inverter selection - targets active system
    updateInverterData: (state, action: PayloadAction<{
      selected_type?: InverterType;
      selected_brand?: {
        name: string;
        inverter_type: InverterType;
      };
      selected_inverters?: SelectedInverter[];
      selected_optimizer?: {
        type: string;
        current?: number;
      };
      optimizer_quantity?: number;
      rsd?: {
        type: string;
        quantity: number;
        current: string;
      };
      combiner?: string;
    }>) => {
      const system = state.form_data.equipment.active_system;
      if ('selected_type' in action.payload) {
        state.form_data.equipment[system].inverters.selected_type = action.payload.selected_type;
      }
      if ('selected_brand' in action.payload) {
        state.form_data.equipment[system].inverters.selected_brand = action.payload.selected_brand;
      }
      if (action.payload.selected_inverters !== undefined) {
        state.form_data.equipment[system].inverters.selected_inverters = action.payload.selected_inverters;
      }
      if ('selected_optimizer' in action.payload) {
        state.form_data.equipment[system].inverters.selected_optimizer = action.payload.selected_optimizer;
      }
      if (action.payload.optimizer_quantity !== undefined) {
        state.form_data.equipment[system].inverters.optimizer_quantity = action.payload.optimizer_quantity;
      }
      if (action.payload.rsd !== undefined) {
        state.form_data.equipment[system].inverters.rsd = action.payload.rsd;
      }
      if (action.payload.combiner !== undefined) {
        state.form_data.equipment[system].inverters.combiner = action.payload.combiner;
      }
    },

    addInverterSelection: (state, action: PayloadAction<SelectedInverter>) => {
      const system = state.form_data.equipment.active_system;
      state.form_data.equipment[system].inverters.selected_inverters.push(action.payload);
    },

    removeInverterSelection: (state, action: PayloadAction<number>) => {
      const system = state.form_data.equipment.active_system;
      state.form_data.equipment[system].inverters.selected_inverters.splice(action.payload, 1);
    },

    // Battery selection - targets active system
    updateBatteryData: (state, action: PayloadAction<{
      selected_brand?: string;
      selected_battery?: {
        battery_name: string;
        battery_model: string;
        kwh?: number;
        current?: number;
        wattage?: number;
      };
      quantity?: number;
      is_domestic?: boolean;
    }>) => {
      const system = state.form_data.equipment.active_system;
      if ('selected_brand' in action.payload) {
        state.form_data.equipment[system].batteries.selected_brand = action.payload.selected_brand;
      }
      if ('selected_battery' in action.payload) {
        state.form_data.equipment[system].batteries.selected_battery = action.payload.selected_battery;
      }
      if (action.payload.quantity !== undefined) {
        state.form_data.equipment[system].batteries.quantity = action.payload.quantity;
      }
      if (action.payload.is_domestic !== undefined) {
        state.form_data.equipment[system].batteries.is_domestic = action.payload.is_domestic;
      }
    },

    // Production data - stays at equipment level (not per-system)
    updateProductionData: (state, action: PayloadAction<{
      production_value?: string;
    }>) => {
      Object.assign(state.form_data.equipment.production, action.payload);
    },

    // Strings data - targets active system
    updateStringsData: (state, action: PayloadAction<Record<string, string>>) => {
      const system = state.form_data.equipment.active_system;
      state.form_data.equipment[system].strings = action.payload;
    },

    // General Configuration data
    updateGeneralConfigurationData: (state, action: PayloadAction<{
      mount_system?: string;
      roof_mount_conduit_size?: string;
      roof_mount_conduit_type?: string;
      bos_conduit?: string;
      trench?: {
        conduit_size?: string;
        conduit_type?: string;
        length?: string;
        depth?: string;
      };
    }>) => {
      Object.assign(state.form_data.structural.general_configuration, action.payload);
    },

    // Bill of Material data — writes to bom.standard_* fields
    updateBillOfMaterialData: (state, action: PayloadAction<{
      total_rails?: number;
      total_splice_kits?: number;
      total_mid_clamps?: number;
      total_end_clamps?: number;
      total_grounding_lugs?: number;
      is_manual_override?: boolean;
    }>) => {
      const p = action.payload;
      const s = state.form_data.bom.standard;
      if (p.total_rails !== undefined) s.total_rails = p.total_rails;
      if (p.total_splice_kits !== undefined) s.total_splice_kits = p.total_splice_kits;
      if (p.total_mid_clamps !== undefined) s.total_mid_clamps = p.total_mid_clamps;
      if (p.total_end_clamps !== undefined) s.total_end_clamps = p.total_end_clamps;
      if (p.total_grounding_lugs !== undefined) s.total_grounding_lugs = p.total_grounding_lugs;
      if (p.is_manual_override !== undefined) s.is_manual_override = p.is_manual_override;
    },

    // Enphase BOM values — populated when any system uses Enphase inverter
    updateEnphaseData: (state, action: PayloadAction<{
      cable_landscape?: number;
      cable_female_connector?: number;
      cable_male_connector?: number;
      cable_sealing_cap?: number;
      cable_terminator_cap?: number;
    }>) => {
      const p = action.payload;
      if (!state.form_data.bom.lgcy.bom_results.enphase) {
        state.form_data.bom.lgcy.bom_results.enphase = {
          cable_landscape: 0, cable_female_connector: 0, cable_male_connector: 0,
          cable_sealing_cap: 0, cable_terminator_cap: 0,
        };
      }
      const e = state.form_data.bom.lgcy.bom_results.enphase;
      if (p.cable_landscape !== undefined) e.cable_landscape = p.cable_landscape;
      if (p.cable_female_connector !== undefined) e.cable_female_connector = p.cable_female_connector;
      if (p.cable_male_connector !== undefined) e.cable_male_connector = p.cable_male_connector;
      if (p.cable_sealing_cap !== undefined) e.cable_sealing_cap = p.cable_sealing_cap;
      if (p.cable_terminator_cap !== undefined) e.cable_terminator_cap = p.cable_terminator_cap;
    },

    // Full LGCY BOM results — synced from useMemo calculation in BOMStep
    updateLgcyBomResults: (state, action: PayloadAction<{
      sum_of_panels: number;
      rails_in_total: number;
      end_cap: number;
      splice_kit: number;
      mid_clamps_total: number;
      end_clamps_total: number;
      washer_lag: number;
      estimated_attachments: number;
      universal_wire_clamp: number;
      wire_retention_clip: number;
      junction_box: number;
      grounding_lugs: number;
      enphase: {
        cable_landscape: number;
        cable_female_connector: number;
        cable_male_connector: number;
        cable_sealing_cap: number;
        cable_terminator_cap: number;
      } | null;
    }>) => {
      const r = state.form_data.bom.lgcy.bom_results;
      if (!r.is_manual_override) {
        Object.assign(r, action.payload);
      } else if (r.enphase === null && action.payload.enphase !== null) {
        // Even when overridden, populate enphase if it hasn't been set yet
        r.enphase = action.payload.enphase;
      }
    },

    // Manual edit of a single LGCY BOM result field — sets is_manual_override
    // Supports dotted paths for nested fields (e.g. 'enphase.cable_landscape')
    setLgcyBomResultField: (state, action: PayloadAction<{ key: string; value: number }>) => {
      const r = state.form_data.bom.lgcy.bom_results;
      const { key, value } = action.payload;

      if (key.startsWith('enphase.') && r.enphase) {
        const enphaseKey = key.split('.')[1];
        (r.enphase as Record<string, number>)[enphaseKey] = value;
      } else {
        (r as Record<string, unknown>)[key] = value;
      }

      r.is_manual_override = true;
    },

    // Update Structure data
    updateStructureData: (state, action: PayloadAction<{
      structureNumber: 1 | 2 | 3;
      data: {
        name?: string;
        framing?: {
          size?: string;
          type?: string;
          rafter_spacing?: string;
          is_assumed?: boolean;
        };
        attachments_rails?: {
          attachment_1?: string;
          rail_1?: {
            name?: string;
            domestic_status?: DomesticStatus;
            is_domestic?: boolean;
            length?: number;
          };
          attachment_2?: string;
          rail_2?: {
            name?: string;
            domestic_status?: DomesticStatus;
            is_domestic?: boolean;
            length?: number;
          };
          panel_rows_1?: {
            rows: Array<{
              id: string;
              orientation: PanelOrientation;
              panel_count: number;
              system_key?: SystemKey;
            }>;
          };
          panel_rows_2?: {
            rows: Array<{
              id: string;
              orientation: PanelOrientation;
              panel_count: number;
              system_key?: SystemKey;
            }>;
          };
        };
        roof_configuration?: {
          roof_type?: string;
          roof_condition?: string;
          reroof_status?: string;
          building_height?: string;
        };
      };
    }>) => {
      const { structureNumber, data } = action.payload;
      const structureKey = `structure_${structureNumber}` as 'structure_1' | 'structure_2' | 'structure_3';

      // Deep merge the structure data
      if (!state.form_data.structural.structures[structureKey]) {
        state.form_data.structural.structures[structureKey] = {};
      }

      const existingStructure = state.form_data.structural.structures[structureKey]!;

      // Merge name
      if (data.name !== undefined) {
        existingStructure.name = data.name;
      }

      // Merge framing
      if (data.framing) {
        if (!existingStructure.framing) {
          existingStructure.framing = {};
        }
        Object.assign(existingStructure.framing, data.framing);
      }

      // Merge attachments_rails (includes panel_rows_1 and panel_rows_2)
      if (data.attachments_rails) {
        if (!existingStructure.attachments_rails) {
          existingStructure.attachments_rails = {};
        }
        Object.assign(existingStructure.attachments_rails, data.attachments_rails);
      }

      // Merge roof_configuration
      if (data.roof_configuration) {
        if (!existingStructure.roof_configuration) {
          existingStructure.roof_configuration = {};
        }
        Object.assign(existingStructure.roof_configuration, data.roof_configuration);
      }
    },


    // MSP data
    updateMspData: (state, action: PayloadAction<{
      buss_rating?: number;
      main_breaker_rating?: number;
      manufacturer?: string;
      location?: string;
      style?: string;
      type?: string;
      cad_location?: string;
      msp_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: 'N' | 'E' | null;
    }>) => {
      Object.assign(state.form_data.electrical.msp, action.payload);
    },

    // MBE data
    updateMbeData: (state, action: PayloadAction<{
      buss_rating?: number;
      main_breaker_rating?: number;
      mbe_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: 'N' | 'E' | null;
    }>) => {
      Object.assign(state.form_data.electrical.mbe, action.payload);
    },

    // BLP data
    updateBlpData: (state, action: PayloadAction<{
      buss_rating?: number;
      main_breaker_rating?: number;
      blp_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: 'N' | 'E' | null;
    }>) => {
      Object.assign(state.form_data.electrical.blp, action.payload);
    },

    // ATS data
    updateAtsData: (state, action: PayloadAction<{
      buss_rating?: number;
      main_breaker_rating?: number;
      ats_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: 'N' | 'E' | null;
      panel_name?: string;
      generator_name?: string;
    }>) => {
      Object.assign(state.form_data.electrical.ats, action.payload);
    },

    // Production Meter data
    updateProductionMeterData: (state, action: PayloadAction<{
      new_existing?: NewExistingType | null;
      meter_name_model?: string;
    }>) => {
      Object.assign(state.form_data.electrical.production_meter, action.payload);
    },

    // Utility data
    updateUtilityData: (state, action: PayloadAction<{
      service_feed_source?: string;
      distance_from_um_callout?: string;
      utility_meter_base_new_existing?: NewExistingType | null;
      utility_meter_base_name_model?: string;
    }>) => {
      Object.assign(state.form_data.electrical.utility, action.payload);
    },

    // Subpanel data
    updateSubpanelData: (state, action: PayloadAction<{
      subpanel_new_existing?: NewExistingType | null;
      main_breaker_new_existing?: 'N' | 'E' | null;
      subpanel_size?: string;
      main_breaker_rating?: number;
      feeding_method?: string;
      ocpd_size?: string;
      pv_breaker_size?: string;
      pv_breaker_location?: string;
    }>) => {
      Object.assign(state.form_data.electrical.subpanel, action.payload);
    },

    // POI data (electrical)
    updatePoiData: (state, action: PayloadAction<{
      selected_poi?: string;
      location?: string;
      breaker_size?: number;
    }>) => {
      Object.assign(state.form_data.electrical.poi, action.payload);
    },

    // DISCO data (electrical) - Updated for multiple disco support
    updateDiscoData: (state, action: PayloadAction<{
      disco_number: 1 | 2 | 3 | 4 | 5;
      data: {
        rating?: number;
        type?: string;
        model?: string;
        fuses?: number;
        location?: string;
        text?: string;
      };
    }>) => {
      const { disco_number, data } = action.payload;
      const discoKey = `disco_${disco_number}` as 'disco_1' | 'disco_2' | 'disco_3' | 'disco_4' | 'disco_5';
      state.form_data.electrical[discoKey] = {
        ...state.form_data.electrical[discoKey],
        ...data
      };
    },

    // Additional Electric Configuration data
    updateAdditionalElectricConfigData: (state, action: PayloadAction<{
      aec_type?: ElectricConfig;
      new_main_breaker_size?: ElectricConfig;
      subpanel_size?: ElectricConfig;
      feeding_method?: ElectricConfig;
      ocpd_size?: ElectricConfig;
      pv_breaker_size?: ElectricConfig;
      pv_breaker_location?: ElectricConfig;
    }>) => {
      Object.assign(state.form_data.electrical, action.payload);
    },

    // BOM LGCY data — updates lgcy sub-state
    updateBomData: (state, action: PayloadAction<Partial<CreateCRState['form_data']['bom']['lgcy']>>) => {
      // When lender changes, clear package and items
      if (action.payload.lender && action.payload.lender.id !== state.form_data.bom.lgcy.lender.id) {
        state.form_data.bom.lgcy.package = { id: '', name: '', key: '' };
        state.form_data.bom.lgcy.lender_package_items = [];
      }
      Object.assign(state.form_data.bom.lgcy, action.payload);
    },

    setBomType: (state, action: PayloadAction<BomType>) => {
      state.form_data.bom.bom_type = action.payload;
    },

    resetBom: (state) => {
      state.form_data.bom = { ...initialState.form_data.bom };
    },

    setPackageItems: (state, action: PayloadAction<PackageItemState[]>) => {
      state.form_data.bom.lgcy.lender_package_items = action.payload;
    },

    togglePackageItemEnabled: (state, action: PayloadAction<string>) => {
      const item = state.form_data.bom.lgcy.lender_package_items.find((i: PackageItemState) => i.id === action.payload);
      if (item) {
        item.enabled = !item.enabled;
      }
    },

    updatePackageItemQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const item = state.form_data.bom.lgcy.lender_package_items.find((i: PackageItemState) => i.id === action.payload.id);
      if (item) {
        item.quantity = action.payload.quantity;
      }
    },

    toggleBomItemEnabled: (state, action: PayloadAction<{ itemId: string; bomItemId: string }>) => {
      const item = state.form_data.bom.lgcy.lender_package_items.find((i: PackageItemState) => i.id === action.payload.itemId);
      if (item) {
        const bomItem = item.bom_items.find((b: { id: string; enabled: boolean }) => b.id === action.payload.bomItemId);
        if (bomItem) {
          bomItem.enabled = !bomItem.enabled;
        }
      }
    },

    setAllBomItemsEnabled: (state, action: PayloadAction<{ itemId: string; enabled: boolean }>) => {
      const item = state.form_data.bom.lgcy.lender_package_items.find((i: PackageItemState) => i.id === action.payload.itemId);
      if (item) {
        item.bom_items.forEach((bom: { enabled: boolean }) => { bom.enabled = action.payload.enabled; });
      }
    },

    updateBomItemQuantity: (state, action: PayloadAction<{ itemId: string; bomItemId: string; quantity: number }>) => {
      const item = state.form_data.bom.lgcy.lender_package_items.find((i: PackageItemState) => i.id === action.payload.itemId);
      if (item) {
        const bomItem = item.bom_items.find((b: { id: string; quantity: number }) => b.id === action.payload.bomItemId);
        if (bomItem) {
          bomItem.quantity = action.payload.quantity;
        }
      }
    },

    /** Replaces the entire client NetSuite item selection (e.g. BOM CSV re-import) */
    setNetsuiteItems: (state, action: PayloadAction<NetsuiteItemState[]>) => {
      state.form_data.bom.lgcy.client_netsuite_items = action.payload;
    },

    addNetsuiteItem: (state, action: PayloadAction<NetsuiteItemState>) => {
      const exists = state.form_data.bom.lgcy.client_netsuite_items.some((i: NetsuiteItemState) => i.id === action.payload.id);
      if (!exists) {
        state.form_data.bom.lgcy.client_netsuite_items.push(action.payload);
      }
    },

    removeNetsuiteItem: (state, action: PayloadAction<string>) => {
      state.form_data.bom.lgcy.client_netsuite_items =
        state.form_data.bom.lgcy.client_netsuite_items.filter((i: NetsuiteItemState) => i.id !== action.payload);
    },

    updateNetsuiteItemQuantity: (state, action: PayloadAction<{ id: string; quantity: number; markEdited?: boolean }>) => {
      const item = state.form_data.bom.lgcy.client_netsuite_items.find((i: NetsuiteItemState) => i.id === action.payload.id);
      if (item) {
        item.quantity = action.payload.quantity;
        if (action.payload.markEdited) {
          item.quantity_edited = true;
        }
      }
    },

    setNetsuiteItemEnabled: (state, action: PayloadAction<{ id: string; enabled: boolean }>) => {
      const item = state.form_data.bom.lgcy.client_netsuite_items.find((i: NetsuiteItemState) => i.id === action.payload.id);
      if (item) {
        item.enabled = action.payload.enabled;
      }
    },

    // Documentation data
    updateDocumentationData: (state, action: PayloadAction<Partial<CreateCRState['form_data']['documentation']>>) => {
      Object.assign(state.form_data.documentation, action.payload);
    },

    // CR Output updates
    setCROutput: (state, action: PayloadAction<{
      generated_cr?: Record<string, any>;
      updated_cr?: Record<string, any>;
      changes?: Record<string, any>;
    }>) => {
      if (action.payload.generated_cr !== undefined) {
        state.cr_output.generated_cr = action.payload.generated_cr;
      }
      if (action.payload.updated_cr !== undefined) {
        state.cr_output.updated_cr = action.payload.updated_cr;
      }
      if (action.payload.changes !== undefined) {
        state.cr_output.changes = action.payload.changes;
      }
    },

    setActiveTab: (state, action: PayloadAction<CROutputTab>) => {
      state.cr_output.active_tab = action.payload;
    },

    // Apply Aurora system configuration — writes to specific systems bypassing active_system
    applyAuroraSystemConfig: (state, action: PayloadAction<{
      systems: Partial<Record<SystemKey, Partial<SystemEquipment>>>;
      production?: string;
    }>) => {
      const { systems, production } = action.payload;

      // Reset all systems first so moved items don't leave stale data
      SYSTEM_KEYS.forEach(key => {
        state.form_data.equipment[key].module = { selected_brand: '', selected_module: {}, quantity: 0 };
        state.form_data.equipment[key].inverters = {
          selected_type: InverterType.INVERTER,
          selected_inverters: [],
          optimizer_quantity: 0,
          rsd: { type: '', quantity: 0, current: '' },
          combiner: '',
        };
      });

      for (const [systemKey, data] of Object.entries(systems)) {
        const key = systemKey as SystemKey;
        if (!data) continue;

        if (data.module) {
          Object.assign(state.form_data.equipment[key].module, data.module);
        }
        if (data.inverters) {
          Object.assign(state.form_data.equipment[key].inverters, data.inverters);
        }
      }

      if (production) {
        state.form_data.equipment.production.production_value = production;
      }
    },

    // Hydrate state from a saved CR for resume-in-workflow
    hydrateFromSavedCR: (state, action: PayloadAction<{
      task_id: string;
      task_name: string;
      structured_data: Record<string, any>;
      aurora_full_data: Record<string, any> | null;
      cr_output: {
        generated_cr: Record<string, any>;
        updated_cr: Record<string, any>;
        changes: Record<string, any>;
      };
    }>) => {
      const { task_id, task_name, structured_data, aurora_full_data, cr_output } = action.payload;

      // Restore workflow navigation state
      if (structured_data.workflow) {
        state.workflow.current_step = structured_data.workflow.current_step || WorkflowStep.TASK;
        state.workflow.completed_steps = structured_data.workflow.completed_steps || [];
      }

      // Restore task data with selected_task stub
      const taskData = structured_data.task || {};
      state.form_data.task.customer_info = taskData.customer_info || initialState.form_data.task.customer_info;
      state.form_data.task.aurora_design_id = taskData.aurora_design_id || '';

      // Always derive selected_task from CR entity fields (single source of truth)
      // Restore client identity from saved bom data (both new and legacy formats)
      if (task_id && task_name) {
        const savedBom = structured_data.bom || structured_data.lender_packages;
        state.form_data.task.selected_task = {
          gid: task_id,
          name: task_name,
          client: savedBom?.client || undefined,
          client_id: savedBom?.client_id || undefined,
          client_name: savedBom?.client_name || undefined,
        };
      }

      // Restore equipment data
      if (structured_data.equipment) {
        state.form_data.equipment = {
          ...initialState.form_data.equipment,
          ...structured_data.equipment,
        };
      }

      // Restore electrical data
      if (structured_data.electrical) {
        state.form_data.electrical = {
          ...initialState.form_data.electrical,
          ...structured_data.electrical,
        };
      }

      // Restore structural data
      if (structured_data.structural) {
        state.form_data.structural = {
          ...initialState.form_data.structural,
          ...structured_data.structural,
        };
      }

      // Restore BOM data — handle both new nested structure and legacy flat format
      if (structured_data.bom) {
        const saved = structured_data.bom;
        state.form_data.bom.bom_type = saved.bom_type || 'standard';

        // Standard BOM
        if (saved.standard) {
          state.form_data.bom.standard = { ...initialState.form_data.bom.standard, ...saved.standard };
        }

        // LGCY BOM — new nested format
        if (saved.lgcy) {
          state.form_data.bom.lgcy = { ...initialState.form_data.bom.lgcy, ...saved.lgcy };
        } else {
          // Legacy flat format — migrate to nested
          state.form_data.bom.lgcy = {
            ...initialState.form_data.bom.lgcy,
            installation_method: saved.installation_method ?? initialState.form_data.bom.lgcy.installation_method,
            lis_number: saved.lis_number ?? '',
            ess_config: saved.ess_config ?? '',
            lender: saved.lender ?? { id: '', name: '' },
            package: saved.package ?? { id: '', name: '', key: '' },
            lender_package_items: saved.lender_package_items ?? [],
            client_netsuite_items: saved.client_netsuite_items ?? [],
            input_fields: saved.lgcy_inputs ?? initialState.form_data.bom.lgcy.input_fields,
            bom_results: {
              ...initialState.form_data.bom.lgcy.bom_results,
              ...(saved.lgcy_results ?? {}),
            },
          };
        }
      }

      // Restore documentation data
      if (structured_data.documentation) {
        state.form_data.documentation = {
          ...initialState.form_data.documentation,
          ...structured_data.documentation,
        };
      }

      // Restore Aurora data from aurora_full_data (already stripped at ingestion)
      if (aurora_full_data) {
        state.aurora_data = {
          design_summary: aurora_full_data.design_summary ?? null,
          roof_design_summary: aurora_full_data.roof_design_summary ?? null,
          structures_roofs_details: aurora_full_data.structures_roofs_details ?? null,
          aurora_panel_rows_pool: aurora_full_data.aurora_panel_rows_pool ?? null,
        };
      }

      // Restore CR output
      state.cr_output.generated_cr = cr_output.generated_cr || {};
      state.cr_output.updated_cr = cr_output.updated_cr || {};
      state.cr_output.changes = cr_output.changes || {};

      // Legacy DB records lack workflow metadata — mark all steps completed
      if (!structured_data.workflow && state.form_data.task.selected_task) {
        state.workflow.completed_steps = Object.values(WorkflowStep);
        state.workflow.current_step = WorkflowStep.OUTPUT;
      }
    },

    // Reset the entire form
    resetCreateCR: () => initialState
  }
});

export const {
  setCurrentStep,
  setCompletedSteps,
  addCompletedStep,
  setValidationAttempted,
  setSelectedTask,
  clearSelectedTask,
  setAuroraDesignId,
  setAuroraData,
  clearAuroraData,
  setStructuresRoofsDetails,
  setAuroraPanelRowsPool,
  updateCustomerInfo,
  setActiveSystem,
  updateModuleData,
  updateInverterData,
  addInverterSelection,
  removeInverterSelection,
  updateBatteryData,
  updateProductionData,
  updateStringsData,
  updateGeneralConfigurationData,
  updateBillOfMaterialData,
  updateEnphaseData,
  updateLgcyBomResults,
  setLgcyBomResultField,
  updateStructureData,
  updateMspData,
  updateMbeData,
  updateBlpData,
  updateAtsData,
  updateProductionMeterData,
  updateUtilityData,
  updateSubpanelData,
  updatePoiData,
  updateDiscoData,
  updateAdditionalElectricConfigData,
  updateBomData,
  setBomType,
  resetBom,
  setPackageItems,
  togglePackageItemEnabled,
  updatePackageItemQuantity,
  toggleBomItemEnabled,
  setAllBomItemsEnabled,
  updateBomItemQuantity,
  setNetsuiteItems,
  addNetsuiteItem,
  removeNetsuiteItem,
  updateNetsuiteItemQuantity,
  setNetsuiteItemEnabled,
  updateDocumentationData,
  setCROutput,
  setActiveTab,
  applyAuroraSystemConfig,
  hydrateFromSavedCR,
  resetCreateCR
} = createCRSlice.actions;

export default createCRSlice.reducer;
